/**
 * The one mapping from an alert's stored strings to the words an editor reads.
 *
 * `satisfies Record<Union, string>` is the point, as elsewhere: a surface added to the pipeline's
 * `AlertSurface` fails `tsc` here until someone decides what it is called in Spanish, rather than
 * rendering a raw `chunk_signal` on screen.
 */

import type { IconName } from 'src/components/ui/Icon';
import type { Tone } from 'src/lib/tone';
import type { EditorialAlertEvent, EditorialAlertRule, EditorialAlertSurface } from 'src/types/editorial';

export const ALERT_SURFACE_LABEL = {
  chunk_text: 'tiempo real',
  chunk_signal: 'claims',
  prenota: 'historias',
  relevance: 'relevancia',
  feed_item: 'publicaciones',
} satisfies Record<EditorialAlertSurface, string>;

/** Latency and precision, in that order: the four surfaces trade one for the other. */
export const ALERT_SURFACE_MEANING = {
  chunk_text: 'Coincidió con el audio tal como se dijo, segundos después de decirse. Lo más rápido y lo más ruidoso.',
  chunk_signal: 'Coincidió con lo que el extractor entendió del bloque: temas, entidades y afirmaciones. Sigue siendo en vivo.',
  prenota: 'Coincidió con la historia ya extraída. Minutos después, y limpio.',
  relevance: 'No es texto: el puntaje de una historia en seguimiento cruzó el umbral de acción o colapsó.',
  feed_item: 'Coincidió con una publicación de un sitio suscrito, no con una transmisión. Se revisa cada quince minutos y no depende de que haya señal en vivo.',
} satisfies Record<EditorialAlertSurface, string>;

export const CROSSING_LABEL = {
  crossed_action_threshold: 'cruzó el umbral',
  collapsed: 'colapsó',
  faded: 'se apagó',
} satisfies Record<NonNullable<EditorialAlertEvent['crossing']>, string>;

/**
 * In the order the pipeline reaches them — the order is what teaches the trade-off — with
 * `feed_item` last because it is not on that path at all: it watches what other newsrooms
 * publish, on its own fifteen-minute clock, whether or not anything is on air.
 */
export const ALERT_SURFACE_ORDER: Array<EditorialAlertSurface> = ['chunk_text', 'chunk_signal', 'prenota', 'relevance', 'feed_item'];

/**
 * `relevance` is not a place to search — it's a score on a story already found through `prenota`,
 * configured as a dependent setting rather than offered beside the three real scopes. Anywhere a
 * rule's surfaces are listed as "dónde busca", they go through this filtered order instead of
 * `ALERT_SURFACE_ORDER` so relevance never reads as a fourth ámbito.
 */
export const ALERT_SCOPE_ORDER: Array<EditorialAlertSurface> = ALERT_SURFACE_ORDER.filter(surface => surface !== 'relevance');

export function formatAlertScopes(surfaces: Array<EditorialAlertSurface>): string {
  return surfaces
    .filter(surface => surface !== 'relevance')
    .map(surface => ALERT_SURFACE_LABEL[surface])
    .join(' · ');
}

/**
 * The newsroom-glance figures for the Alertas head. Three real counts, nothing inferred: no
 * "priority" field exists on a rule or an event, so there is no fourth figure standing in for one.
 *
 * `events` is whatever page is already loaded (the history's first `rowsPerPage`) — an honest count
 * of "today, among what's loaded" rather than a silent server round trip a glance figure doesn't
 * warrant.
 */
export type AlertFigures = {
  activeRules: number;
  eventsToday: number;
  eventsUnseen: number;
  mailIssues: number;
};

/**
 * The card's tint, driven by what the rule actually watches — not a decorative per-row rainbow.
 * `relevance` reads as risk (it fires on a story collapsing or crossing the action threshold, the
 * closest thing this domain has to an alarm); `prenota` as the story desk; the two live-text
 * surfaces as a plain informational watch. A rule can watch several; the most consequential wins.
 */
export function ruleMarkTone(surfaces: Array<EditorialAlertSurface>): Tone {
  if (surfaces.includes('relevance')) return 'partial';
  if (surfaces.includes('feed_item') && surfaces.length === 1) return 'social';
  if (surfaces.includes('prenota')) return 'social';
  return 'info';
}

/** The glyph beside `ruleMarkTone`'s tint — same three-way read, drawn rather than a letter
 *  avatar. Only the tones `ruleMarkTone` actually returns are meaningful here. */
export function ruleMarkIcon(tone: Tone): IconName {
  switch (tone) {
    case 'partial':
      return 'alerts';
    case 'social':
      return 'trends';
    default:
      return 'broadcasts';
  }
}

export function buildAlertFigures(rules: Array<EditorialAlertRule>, events: Array<EditorialAlertEvent>, seenAt: number, now: Date): AlertFigures {
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  const startOfDayMs = startOfDay.getTime();

  let eventsToday = 0;
  let eventsUnseen = 0;
  let mailIssues = 0;

  for (const event of events) {
    const occurredAtMs = event.occurredAt ? Date.parse(event.occurredAt) : Number.NaN;
    if (Number.isFinite(occurredAtMs)) {
      if (occurredAtMs >= startOfDayMs) eventsToday += 1;
      if (occurredAtMs > seenAt) eventsUnseen += 1;
    }
    if (event.notificationStatus === 'pending' || event.notificationStatus === 'failed') mailIssues += 1;
  }

  return {
    activeRules: rules.filter(rule => rule.active).length,
    eventsToday,
    eventsUnseen,
    mailIssues,
  };
}
